// ============================================================
// Signup Page — Firebase Email/Password + Google Sign-Up
// ============================================================

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { firebaseSignUpWithEmail, firebaseSignInWithGoogle } from '../lib/firebase';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function SignupPage() {
    const [form, setForm] = useState({ name: '', email: '', password: '', tenantName: '' });
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showTenantForGoogle, setShowTenantForGoogle] = useState(false);
    const [googleIdToken, setGoogleIdToken] = useState('');
    const { setAuth } = useAuthStore();
    const navigate = useNavigate();

    /** Exchange Firebase token with our backend for signup */
    async function firebaseSignup(idToken: string, tenantName: string) {
        const res = await fetch(`${API_BASE}/auth/firebase/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken, tenantName }),
        });
        const data = await res.json();

        if (!res.ok) {
            // If already registered, try login instead
            if (res.status === 409) {
                const loginRes = await fetch(`${API_BASE}/auth/firebase`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idToken }),
                });
                const loginData = await loginRes.json();
                if (!loginRes.ok) throw new Error(loginData.error || 'Login failed');
                return loginData;
            }
            throw new Error(data.error || 'Signup failed');
        }
        return data;
    }

    /** Email/Password Signup */
    const handleEmailSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.password || !form.tenantName) {
            toast.error('Please fill all fields');
            return;
        }
        setLoading(true);
        try {
            // Create Firebase user first
            const idToken = await firebaseSignUpWithEmail(form.email, form.password);

            // Then register in our backend
            const data = await firebaseSignup(idToken, form.tenantName);

            setAuth({
                user: data.data.user,
                tenant: data.data.tenant,
                accessToken: data.data.accessToken,
                refreshToken: data.data.refreshToken,
                role: 'owner',
            });

            toast.success('Account created! Welcome to HireHub.');
            navigate('/dashboard');
        } catch (err: any) {
            const msg = err.code === 'auth/email-already-in-use' ? 'Email already in use. Try signing in.'
                : err.code === 'auth/weak-password' ? 'Password must be at least 6 characters'
                    : err.message || 'Signup failed';
            toast.error(msg);
        }
        setLoading(false);
    };

    /** Google Sign-Up — Step 1: Get token, Step 2: Ask for company name */
    const handleGoogleSignup = async () => {
        setGoogleLoading(true);
        try {
            const { idToken } = await firebaseSignInWithGoogle();

            // First try login (user might already exist)
            const loginRes = await fetch(`${API_BASE}/auth/firebase`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken }),
            });
            const loginData = await loginRes.json();

            if (loginRes.ok) {
                // User exists — just log them in
                setAuth({
                    user: loginData.data.user,
                    tenant: loginData.data.tenant,
                    accessToken: loginData.data.accessToken,
                    refreshToken: loginData.data.refreshToken,
                    role: loginData.data.user.role,
                });
                toast.success('Welcome back!');
                navigate('/dashboard');
                return;
            }

            // New user — need company name
            setGoogleIdToken(idToken);
            setShowTenantForGoogle(true);
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user') {
                toast.error(err.message || 'Google sign-up failed');
            }
        }
        setGoogleLoading(false);
    };

    /** Google Sign-Up — Step 2: Submit with company name */
    const handleGoogleTenantSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.tenantName) {
            toast.error('Please enter your company name');
            return;
        }
        setLoading(true);
        try {
            const data = await firebaseSignup(googleIdToken, form.tenantName);
            setAuth({
                user: data.data.user,
                tenant: data.data.tenant,
                accessToken: data.data.accessToken,
                refreshToken: data.data.refreshToken,
                role: 'owner',
            });
            toast.success('Account created! Welcome to HireHub.');
            navigate('/dashboard');
        } catch (err: any) {
            toast.error(err.message || 'Signup failed');
        }
        setLoading(false);
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--color-bg-primary)',
        }}>
            <div className="card" style={{ width: 420, padding: 40 }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 14, margin: '0 auto 16px',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 800, fontSize: 22,
                    }}>H</div>
                    <h1 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: 4 }}>Create your account</h1>
                    <p style={{ color: '#6b7280', fontSize: 14 }}>Start automating your hiring today</p>
                </div>

                {showTenantForGoogle ? (
                    /* Google signup step 2: get company name */
                    <form onSubmit={handleGoogleTenantSubmit}>
                        <div style={{
                            padding: '12px 16px', background: 'rgba(99,102,241,0.1)', borderRadius: 8,
                            marginBottom: 20, fontSize: 13, color: '#6366f1', textAlign: 'center',
                        }}>
                            ✓ Google account connected. Just one more step!
                        </div>
                        <div className="form-group">
                            <label className="form-label">Company / Organization Name *</label>
                            <input className="form-input" value={form.tenantName}
                                onChange={(e) => setForm({ ...form, tenantName: e.target.value })}
                                placeholder="Acme Inc." required autoFocus />
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
                            disabled={loading}>
                            {loading ? 'Creating...' : 'Create Account'}
                        </button>
                    </form>
                ) : (
                    <>
                        {/* Google Sign-Up */}
                        <button
                            onClick={handleGoogleSignup}
                            disabled={googleLoading}
                            style={{
                                width: '100%', padding: '12px 16px', borderRadius: 10,
                                border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
                                color: 'var(--color-text-primary)', fontWeight: 600, fontSize: 14,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                marginBottom: 20, transition: 'border-color 0.2s',
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            {googleLoading ? 'Connecting...' : 'Continue with Google'}
                        </button>

                        {/* Divider */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                            <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                            <span style={{ fontSize: 12, color: '#6b7280' }}>or</span>
                            <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                        </div>

                        {/* Email/Password Form */}
                        <form onSubmit={handleEmailSignup}>
                            <div className="form-group">
                                <label className="form-label">Your Name</label>
                                <input className="form-input" value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="John Doe" required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Company Name</label>
                                <input className="form-input" value={form.tenantName}
                                    onChange={(e) => setForm({ ...form, tenantName: e.target.value })}
                                    placeholder="Acme Inc." required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <input className="form-input" type="email" value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="you@company.com" required />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Password</label>
                                <input className="form-input" type="password" value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    placeholder="Min 6 characters" required minLength={6} />
                            </div>
                            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
                                disabled={loading}>
                                {loading ? 'Creating...' : 'Create Account'}
                            </button>
                        </form>
                    </>
                )}

                <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#6b7280' }}>
                    Already have an account?{' '}
                    <Link to="/login" style={{ color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}>
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
